import type { JobCandidate } from '../api/data-source'
import { ETA_MINUTES_PER_KM, INCLUDE_BUSY_WORKERS, NEARBY_RADIUS_KM } from '../schedulingRules'
import { ANY_CATEGORY, type Job, type Worker } from '../types'
import { hasValidCoords, haversineKm } from './distance'
import { findWorkerConflicts, jobHasWorker } from './job-workers'

export interface NearbyCandidate {
  worker: Worker
  distanceKm: number
  etaMinutes: number
  /** Worker already has an overlapping job: shown as a Conflict chip, Add asks to confirm. */
  hasConflict: boolean
  /** The overlapping jobs, when known locally (listed in the confirm dialog). */
  conflicts: Job[]
}

export interface NearbyOptions {
  radiusKm?: number
  includeBusy?: boolean
  /** Driving ETAs (Distance Matrix) by worker id; straight-line estimate otherwise. */
  etaOverrides?: Map<string, number>
}

/** Straight-line ETA estimate (~3 min/km), used when Distance Matrix is off or fails. */
export function estimateStraightLineEtaMinutes(distanceKm: number): number {
  return Math.max(1, Math.round(distanceKm * ETA_MINUTES_PER_KM))
}

/**
 * Mirrors the backend rule (worker_category_rules.py): a job without a category accepts any
 * worker; otherwise the worker needs that category or an any-category skill.
 */
export function workerMatchesCategory(worker: Pick<Worker, 'categories'>, categoryId: string | null | undefined): boolean {
  if (!categoryId) return true
  const categories = worker.categories ?? []
  return categories.includes(ANY_CATEGORY) || categories.includes(categoryId)
}

/** Free to take work right now: not offline, not on leave and (unless busy workers are allowed) not on a job. */
export function isWorkerAvailable(worker: Worker, includeBusy = INCLUDE_BUSY_WORKERS): boolean {
  if (worker.onLeave || worker.status === 'offline') return false
  return includeBusy || worker.status === 'available'
}

/**
 * Nearby workers for a job, nearest first: same category, available, not already on the job,
 * located within `radiusKm` of the job. Jobs without coordinates have no nearby workers.
 * TODO(backend): GET /api/jobs/:id/candidates?radiusKm= should own this ranking; the board
 * then only refines the server list with live locations/availability (see mergeServerCandidates).
 */
export function findNearbyCandidates(
  job: Job,
  workers: Worker[],
  jobs: Job[],
  { radiusKm = NEARBY_RADIUS_KM, includeBusy = INCLUDE_BUSY_WORKERS, etaOverrides }: NearbyOptions = {},
): NearbyCandidate[] {
  if (!hasValidCoords(job)) return []
  const candidates: NearbyCandidate[] = []
  workers.forEach((worker) => {
    if (!hasValidCoords(worker)) return
    if (jobHasWorker(job, worker.id) || !workerMatchesCategory(worker, job.categoryId) || !isWorkerAvailable(worker, includeBusy)) return
    const distanceKm = haversineKm(worker, job)
    if (distanceKm > radiusKm) return
    // Conflicts only for workers in range: this keeps per-job counts cheap.
    const conflicts = jobs.length > 0 ? findWorkerConflicts(job, worker.id, jobs) : []
    candidates.push({
      worker,
      distanceKm,
      etaMinutes: etaOverrides?.get(worker.id) ?? estimateStraightLineEtaMinutes(distanceKm),
      hasConflict: conflicts.length > 0,
      conflicts,
    })
  })
  return candidates.sort(compareNearby)
}

export function compareNearby(a: NearbyCandidate, b: NearbyCandidate): number {
  return a.distanceKm - b.distanceKm || a.worker.name.localeCompare(b.worker.name)
}

/** Number of nearby workers (for the "Assign · N nearby" button on collapsed cards). */
export function countNearbyCandidates(job: Job, workers: Worker[], options?: NearbyOptions): number {
  return findNearbyCandidates(job, workers, [], options).length
}

/**
 * Server candidates decide who is eligible; live local data still drops anyone who went
 * offline/busy/moved out of range and supplies current distances. Server ETA and conflict
 * flags are used when present.
 */
export function mergeServerCandidates(local: NearbyCandidate[], server: Map<string, JobCandidate>, etaOverrides?: Map<string, number>): NearbyCandidate[] {
  return local
    .filter((candidate) => server.has(candidate.worker.id))
    .map((candidate) => {
      const remote = server.get(candidate.worker.id) as JobCandidate
      return {
        ...candidate,
        etaMinutes: etaOverrides?.get(candidate.worker.id) ?? remote.etaMinutes ?? candidate.etaMinutes,
        hasConflict: candidate.hasConflict || remote.hasConflict,
      }
    })
    .sort(compareNearby)
}
